import { useState } from 'react'
import { Button, Grid, Typography, CircularProgress, Box } from '@mui/material'

import { useListKnowledgeBaseCollectionsQuery, useDeleteKnowledgeBaseCollectionMutation } from '@/core/services/knowledge-base'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import CollectionCard from '../components/CollectionCard'
import CreateCollectionModal from '../components/CreateCollectionModal'
import { Trans } from '@lingui/macro'
import { useSnackbar } from 'notistack'
import { getApiErrorMessage } from '@/utils/errors'
import { KnowledgeBaseCollection } from '@/core/models/knowledge-base-collection.model'

function CollectionsDashboard() {
  const authUser = useAppSelector(selectAuthUser)
  const establishmentId = authUser?.establishment?._id

  const {
    data: collections,
    isLoading,
    refetch,
  } = useListKnowledgeBaseCollectionsQuery(
    { query: { establishmentId: establishmentId! }, options: { limit: 10, skip: 0, order: 'ASC' } },
    { skip: !establishmentId }
  )
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [deleteCollection, { isLoading: isDeleting }] = useDeleteKnowledgeBaseCollectionMutation()
  const { enqueueSnackbar } = useSnackbar()

  const handleDelete = async (collection: KnowledgeBaseCollection) => {
    if (window.confirm(`Are you sure you want to delete the collection "${collection.name}"? This will delete all associated documents and is irreversible.`)) {
      try {
        await deleteCollection(collection._id).unwrap()
        enqueueSnackbar('Collection deleted successfully', { variant: 'success' })
        refetch()
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, 'Failed to delete collection'), { variant: 'error' })
      }
    }
  }

  if (isLoading) {
    return <CircularProgress />
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <Trans>Knowledge Base</Trans>
          </Typography>
          <Typography color="text.secondary">
            <Trans>Manage your establishment's knowledge collections.</Trans>
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setCreateModalOpen(true)}>
          <Trans>Create Collection</Trans>
        </Button>
      </Box>

      {collections && collections.items.length > 0 ? (
        <Grid container spacing={3}>
          {collections.items.map((collection) => (
            <Grid item xs={12} sm={6} md={4} key={collection._id}>
              <CollectionCard collection={collection} onDelete={handleDelete} isDeleting={isDeleting} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography>
          <Trans>No collections found. Get started by creating one.</Trans>
        </Typography>
      )}

      <CreateCollectionModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} />
    </>
  )
}

export default CollectionsDashboard
